import React, { useMemo, useState } from 'react';
import { Check, FolderOpen, Search, X } from 'lucide-react';
import { SOURCE_KIND_OPTIONS, SOURCE_META, SPACE_ICON_OPTIONS } from '../dataModelsConstants';
import { logDataModels } from '../dataModelsLogger';
import type { DataModel, DataSourceKind, PublishStatus, Space, SpaceIconKey, SpaceStatus } from '../dataModelsTypes';
import ConnectionAssetBrowser, { type DiscoveredModelAsset } from './ConnectionAssetBrowser';
import CreateSpacePicker, { TagEditor, type CreateSpaceInput } from './CreateSpacePicker';
import { DialogShell, SourceIconTile, fieldClass, ghostBtn, iconBtn, labelClass, primaryBtn } from './dataModelsUi';

export type CreateModelInput = {
  name: string;
  description: string;
  tags: string[];
  status: PublishStatus;
  assetIds: string[];
  discoveredAssets?: DiscoveredModelAsset[];
};

export type { DiscoveredModelAsset };

export type ModelAssetOption = {
  id: string;
  name: string;
  sourceId: string;
  sourceName: string;
  sourceKind: DataSourceKind;
  sourceEnvironment: string;
};

export function CreateSpaceDialog({
  isLight,
  onClose,
  onSubmit,
}: {
  isLight: boolean;
  onClose: () => void;
  onSubmit: (input: CreateSpaceInput) => void;
}) {
  return <CreateSpacePicker isLight={isLight} onClose={onClose} onSubmit={onSubmit} />;
}

export function EditSpaceDialog({
  isLight,
  space,
  onClose,
  onSubmit,
}: {
  isLight: boolean;
  space: Space;
  onClose: () => void;
  onSubmit: (input: { name: string; description: string; icon: SpaceIconKey; status: SpaceStatus }) => void;
}) {
  const [name, setName] = useState(space.name);
  const [description, setDescription] = useState(space.description);
  const [icon, setIcon] = useState<SpaceIconKey>(space.icon);
  const [status, setStatus] = useState<SpaceStatus>(space.status);
  const valid = Boolean(name.trim());

  return (
    <DialogShell
      title="Edit WorkSpace"
      subtitle="Update the name, description, and status."
      isLight={isLight}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={primaryBtn()}
            disabled={!valid}
            onClick={() => onSubmit({ name: name.trim(), description: description.trim(), icon, status })}
          >
            Save WorkSpace
          </button>
        </>
      }
    >
      <div>
        <label className={labelClass(isLight)}>Name</label>
        <input className={fieldClass(isLight)} value={name} onChange={(event) => setName(event.target.value)} />
      </div>
      <div>
        <label className={labelClass(isLight)}>Description</label>
        <textarea
          className={`${fieldClass(isLight)} min-h-[88px] resize-none`}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass(isLight)}>Icon</label>
          <select className={fieldClass(isLight)} value={icon} onChange={(event) => setIcon(event.target.value as SpaceIconKey)}>
            {SPACE_ICON_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass(isLight)}>Status</label>
          <select className={fieldClass(isLight)} value={status} onChange={(event) => setStatus(event.target.value as SpaceStatus)}>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>
    </DialogShell>
  );
}

export function CreateModelDialog({
  isLight,
  spaceName,
  sourceIds,
  onClose,
  onSubmit,
}: {
  isLight: boolean;
  spaceName: string;
  sourceIds: string[];
  onClose: () => void;
  onSubmit: (input: CreateModelInput) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState('');
  const [status, setStatus] = useState<PublishStatus>('draft');
  const [discoveredAssets, setDiscoveredAssets] = useState<DiscoveredModelAsset[]>([]);
  const valid = Boolean(name.trim());

  const addTags = (raw: string) => {
    if (!raw.trim()) return;
    setTags((prev) => mergeTags(raw, prev));
    setTagDraft('');
  };

  return (
    <DialogShell
      title="Create data model"
      subtitle={`Add a model to ${spaceName} and seed it with assets.`}
      isLight={isLight}
      onClose={onClose}
      size="medium"
      footer={
        <>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={primaryBtn()}
            disabled={!valid}
            onClick={() =>
              onSubmit({
                name: name.trim(),
                description: description.trim(),
                tags: mergeTags(tagDraft, tags),
                status,
                assetIds: discoveredAssets.map((asset) => asset.id),
                discoveredAssets,
              })
            }
          >
            Create data model
          </button>
        </>
      }
    >
      <div>
        <label className={labelClass(isLight)}>Name</label>
        <input className={fieldClass(isLight)} value={name} onChange={(event) => setName(event.target.value)} placeholder="Customer 360" />
      </div>
      <div>
        <label className={labelClass(isLight)}>Description</label>
        <textarea
          className={`${fieldClass(isLight)} min-h-[88px] resize-none`}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Unified customer profile with demographics, behavior and value metrics."
        />
      </div>
      <AssetField isLight={isLight} sourceIds={sourceIds} selected={discoveredAssets} onChange={setDiscoveredAssets} />
      <div>
        <label className={labelClass(isLight)}>Tags</label>
        <TagEditor
          isLight={isLight}
          tags={tags}
          draft={tagDraft}
          onDraft={setTagDraft}
          onAdd={addTags}
          onRemove={(tag) => setTags((prev) => prev.filter((item) => item !== tag))}
        />
      </div>
      <div>
        <label className={labelClass(isLight)}>Status</label>
        <select className={fieldClass(isLight)} value={status} onChange={(event) => setStatus(event.target.value as PublishStatus)}>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
      </div>
    </DialogShell>
  );
}

function mergeTags(raw: string, current: string[]): string[] {
  const next = raw.split(',').map((tag) => tag.trim()).filter(Boolean);
  const seen = new Set(current.map((tag) => tag.toLowerCase()));
  const merged = [...current];
  next.forEach((tag) => {
    if (seen.has(tag.toLowerCase()) || merged.length >= 12) return;
    seen.add(tag.toLowerCase());
    merged.push(tag);
  });
  return merged;
}

function AssetField({
  isLight,
  sourceIds,
  selected,
  onChange,
}: {
  isLight: boolean;
  sourceIds: string[];
  selected: DiscoveredModelAsset[];
  onChange: (assets: DiscoveredModelAsset[]) => void;
}) {
  const [explorerOpen, setExplorerOpen] = useState(false);
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-500';

  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <label className={`${labelClass(isLight)} mb-0`}>Assets</label>
        <span className={`text-xs ${muted}`}>{selected.length ? `${selected.length} selected` : 'Optional seed'}</span>
      </div>
      <button
        type="button"
        className={ghostBtn(isLight)}
        onClick={() => {
          setExplorerOpen(true);
          logDataModels('app', 'model_create_explorer_opened', { selected: selected.length });
        }}
      >
        <FolderOpen size={14} />
        Browse assets
      </button>
      {selected.length ? (
        <div className="mt-2 flex max-h-[5.5rem] flex-wrap gap-1.5 overflow-hidden">
          {selected.slice(0, 8).map((asset) => (
            <span key={asset.id} className={`inline-flex items-center gap-1 rounded-md px-2 py-1 font-mono text-[11px] ${isLight ? 'bg-[#F2F4F7] text-[#3C4048]' : 'bg-white/10 text-slate-200'}`}>
              {asset.name}
              <button
                type="button"
                className={`${iconBtn(isLight)} !h-5 !w-5`}
                aria-label={`Remove ${asset.name}`}
                onClick={() => onChange(selected.filter((item) => item.id !== asset.id))}
              >
                <X size={11} />
              </button>
            </span>
          ))}
          {selected.length > 8 ? (
            <button
              type="button"
              className={`inline-flex items-center rounded-md px-2 py-1 text-[11px] ${isLight ? 'bg-[#F2F4F7] text-[#6B7280]' : 'bg-white/10 text-slate-400'}`}
              onClick={() => {
                setExplorerOpen(true);
                logDataModels('app', 'model_create_explorer_opened', { selected: selected.length, source: 'overflow' });
              }}
            >
              … +{selected.length - 8} more
            </button>
          ) : null}
        </div>
      ) : null}
      {explorerOpen ? (
        <ConnectionAssetBrowser
          isLight={isLight}
          sourceIds={sourceIds}
          selected={selected}
          onClose={() => setExplorerOpen(false)}
          onApply={(assets) => {
            onChange(assets);
            setExplorerOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

type SourceGroup = {
  sourceId: string;
  sourceName: string;
  sourceKind: DataSourceKind;
  sourceEnvironment: string;
  assets: ModelAssetOption[];
};

function groupAssets(assets: ModelAssetOption[]): SourceGroup[] {
  const map = new Map<string, SourceGroup>();
  assets.forEach((asset) => {
    const current = map.get(asset.sourceId) ?? {
      sourceId: asset.sourceId,
      sourceName: asset.sourceName,
      sourceKind: asset.sourceKind,
      sourceEnvironment: asset.sourceEnvironment,
      assets: [],
    };
    current.assets.push(asset);
    map.set(asset.sourceId, current);
  });
  return Array.from(map.values()).sort((left, right) => left.sourceName.localeCompare(right.sourceName));
}

export function AssetExplorer({
  isLight,
  assets,
  selectedIds,
  onClose,
  onApply,
  title = 'Select assets',
  subtitle = 'Browse by data source, then pick the assets to add.',
}: {
  isLight: boolean;
  assets: ModelAssetOption[];
  selectedIds: string[];
  onClose: () => void;
  onApply: (ids: string[]) => void;
  title?: string;
  subtitle?: string;
}) {
  const groups = useMemo(() => groupAssets(assets), [assets]);
  const [sourceId, setSourceId] = useState(groups[0]?.sourceId ?? '');
  const [draft, setDraft] = useState(selectedIds);
  const [query, setQuery] = useState('');
  const group = groups.find((item) => item.sourceId === sourceId) ?? groups[0];
  const visible = useMemo(() => {
    if (!group) return [];
    const needle = query.trim().toLowerCase();
    if (!needle) return group.assets;
    return group.assets.filter((asset) => asset.name.toLowerCase().includes(needle));
  }, [group, query]);
  const ids = group?.assets.map((asset) => asset.id) ?? [];
  const selectedCount = ids.filter((id) => draft.includes(id)).length;
  const allOn = selectedCount === ids.length && ids.length > 0;
  const mixed = selectedCount > 0 && !allOn;
  const rail = isLight ? 'border-[#E6E8EB] bg-[#F7F8FA]' : 'border-white/10 bg-white/[0.03]';
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const ink = isLight ? 'text-[#1C1C1C]' : 'text-white';

  const toggle = (id: string) => {
    setDraft((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  };

  const toggleGroup = () => {
    if (!group) return;
    setDraft((current) => (allOn ? current.filter((id) => !ids.includes(id)) : Array.from(new Set([...current, ...ids]))));
    logDataModels('app', 'model_create_assets_toggled', { sourceId: group.sourceId, count: ids.length, selected: !allOn });
  };

  return (
    <DialogShell
      title={title}
      subtitle={subtitle}
      isLight={isLight}
      onClose={onClose}
      size="large"
      footer={
        <>
          <span className={`mr-auto self-center text-[12px] ${muted}`}>
            {draft.length} selected
          </span>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>Cancel</button>
          <button type="button" className={primaryBtn()} onClick={() => onApply(draft)}>Apply</button>
        </>
      }
    >
      <div className="flex h-[min(480px,calc(100vh-16rem))] min-h-[22rem]">
        <nav className={`w-[240px] shrink-0 overflow-y-auto border-r p-2 ${rail}`}>
          <p className={`px-2 pb-2 pt-1 text-[10px] font-semibold uppercase tracking-wide ${muted}`}>Sources</p>
          {groups.map((item) => {
            const active = item.sourceId === (group?.sourceId ?? '');
            const picked = item.assets.filter((asset) => draft.includes(asset.id)).length;
            return (
              <button
                key={item.sourceId}
                type="button"
                onClick={() => {
                  setSourceId(item.sourceId);
                  setQuery('');
                }}
                className={`mb-0.5 flex w-full items-center gap-2 rounded-md px-2 py-2 text-left ${
                  active
                    ? isLight ? 'bg-white text-[#111827] shadow-sm' : 'bg-white/10 text-white'
                    : muted
                }`}
              >
                <SourceIconTile kind={item.sourceKind} isLight={isLight} box="h-6 w-6" />
                <span className="min-w-0">
                  <span className={`block truncate text-[12px] ${active ? 'font-medium' : ''}`}>
                    {item.sourceName}
                    {item.sourceEnvironment ? ` · ${item.sourceEnvironment}` : ''}
                  </span>
                  <span className={`block truncate text-[11px] ${muted}`}>
                    {item.assets.length} asset{item.assets.length === 1 ? '' : 's'}
                    {picked ? ` · ${picked} selected` : ''}
                  </span>
                </span>
              </button>
            );
          })}
          {!groups.length ? <p className={`px-2 py-3 text-[12px] ${muted}`}>No sources available.</p> : null}
        </nav>
        <div className="min-w-0 flex-1 overflow-y-auto px-4 py-3">
          {group ? (
            <>
              <button
                type="button"
                onClick={toggleGroup}
                className={`mb-3 flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left ${isLight ? 'border-[#E6E8EB] hover:bg-[#F7F8FA]' : 'border-white/10 hover:bg-white/5'}`}
              >
                <PickBox checked={allOn} mixed={mixed} isLight={isLight} />
                <span className={`text-[12px] font-medium ${ink}`}>
                  {SOURCE_META[group.sourceKind]?.label ?? group.sourceName}
                  {group.sourceEnvironment ? ` · ${group.sourceEnvironment}` : ''}
                </span>
                <span className={`ml-auto text-[11px] ${muted}`}>{selectedCount}/{group.assets.length}</span>
              </button>
              <label className={`relative mb-3 block`}>
                <Search size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${muted}`} />
                <input
                  autoFocus
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search assets"
                  className={isLight
                    ? 'h-8 w-full rounded-md border border-[#D8DCE2] bg-white py-0 pl-8 pr-3 text-sm text-[#1C1C1C] placeholder:text-[#9AA1AB] focus:border-[#1B6CA8] focus:outline-none'
                    : 'h-8 w-full rounded-md border border-white/15 bg-black py-0 pl-8 pr-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-white/30 focus:outline-none'}
                />
              </label>
              <div className="space-y-0.5">
                {visible.map((asset) => {
                  const checked = draft.includes(asset.id);
                  return (
                    <button
                      key={asset.id}
                      type="button"
                      onClick={() => toggle(asset.id)}
                      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left ${isLight ? 'hover:bg-[#F3F7FB]' : 'hover:bg-white/5'}`}
                    >
                      <PickBox checked={checked} isLight={isLight} />
                      <span className={`truncate font-mono text-xs ${ink}`}>{asset.name}</span>
                    </button>
                  );
                })}
                {!visible.length ? <p className={`px-2 py-3 text-[12px] ${muted}`}>No assets match this search.</p> : null}
              </div>
            </>
          ) : (
            <p className={`py-8 text-center text-[12px] ${muted}`}>No assets available.</p>
          )}
        </div>
      </div>
    </DialogShell>
  );
}

function PickBox({ checked, mixed, isLight }: { checked: boolean; mixed?: boolean; isLight: boolean }) {
  return (
    <span className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
      checked || mixed
        ? 'border-[#1B6CA8] bg-[#1B6CA8] text-white'
        : isLight ? 'border-[#D8DCE2]' : 'border-white/20'
    }`}
    >
      {checked ? <Check size={11} /> : mixed ? <span className="h-0.5 w-2 rounded-sm bg-white" /> : null}
    </span>
  );
}

export function EditModelDialog({
  isLight,
  model,
  onClose,
  onSubmit,
}: {
  isLight: boolean;
  model: DataModel;
  onClose: () => void;
  onSubmit: (input: { name: string; description: string; tags: string[]; status: PublishStatus }) => void;
}) {
  const [name, setName] = useState(model.name);
  const [description, setDescription] = useState(model.description);
  const [tags, setTags] = useState(model.tags.join(', '));
  const [status, setStatus] = useState<PublishStatus>(model.status);
  const valid = Boolean(name.trim());

  return (
    <DialogShell
      title="Edit data model"
      subtitle="Update the name, description, and status."
      isLight={isLight}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>Cancel</button>
          <button
            type="button"
            className={primaryBtn()}
            disabled={!valid}
            onClick={() =>
              onSubmit({
                name: name.trim(),
                description: description.trim(),
                tags: tags.split(',').map((tag) => tag.trim()).filter(Boolean),
                status,
              })
            }
          >
            Save data model
          </button>
        </>
      }
    >
      <div>
        <label className={labelClass(isLight)}>Name</label>
        <input className={fieldClass(isLight)} value={name} onChange={(event) => setName(event.target.value)} />
      </div>
      <div>
        <label className={labelClass(isLight)}>Description</label>
        <textarea className={`${fieldClass(isLight)} min-h-[88px] resize-none`} value={description} onChange={(event) => setDescription(event.target.value)} />
      </div>
      <div>
        <label className={labelClass(isLight)}>Tags</label>
        <input className={fieldClass(isLight)} value={tags} onChange={(event) => setTags(event.target.value)} />
      </div>
      <div>
        <label className={labelClass(isLight)}>Status</label>
        <select className={fieldClass(isLight)} value={status} onChange={(event) => setStatus(event.target.value as PublishStatus)}>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
      </div>
    </DialogShell>
  );
}

export function AddTableDialog({
  isLight,
  modelName,
  sourceOptions,
  onClose,
  onSubmit,
}: {
  isLight: boolean;
  modelName: string;
  sourceOptions: { id: string; name: string; kind: DataSourceKind }[];
  onClose: () => void;
  onSubmit: (input: { name: string; description: string; rows: number; columns: number; sourceId: string; status: PublishStatus }) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rows, setRows] = useState('1000000');
  const [columns, setColumns] = useState('12');
  const [sourceId, setSourceId] = useState(sourceOptions[0]?.id ?? '');
  const [status, setStatus] = useState<PublishStatus>('draft');
  const valid = Boolean(name.trim() && sourceId);

  return (
    <DialogShell
      title="Add asset"
      subtitle={`Register an asset on ${modelName}.`}
      isLight={isLight}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={primaryBtn()}
            disabled={!valid}
            onClick={() =>
              onSubmit({
                name: name.trim(),
                description: description.trim(),
                rows: Number(rows) || 0,
                columns: Number(columns) || 0,
                sourceId,
                status,
              })
            }
          >
            Add asset
          </button>
        </>
      }
    >
      <div>
        <label className={labelClass(isLight)}>Name</label>
        <input className={fieldClass(isLight)} value={name} onChange={(event) => setName(event.target.value)} placeholder="customer_profile" />
      </div>
      <div>
        <label className={labelClass(isLight)}>Description</label>
        <input className={fieldClass(isLight)} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Customer master profile and demographics" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass(isLight)}>Rows</label>
          <input className={fieldClass(isLight)} value={rows} onChange={(event) => setRows(event.target.value)} />
        </div>
        <div>
          <label className={labelClass(isLight)}>Columns</label>
          <input className={fieldClass(isLight)} value={columns} onChange={(event) => setColumns(event.target.value)} />
        </div>
      </div>
      <div>
        <label className={labelClass(isLight)}>Data source</label>
        <select className={fieldClass(isLight)} value={sourceId} onChange={(event) => setSourceId(event.target.value)}>
          {sourceOptions.map((source) => (
            <option key={source.id} value={source.id}>
              {SOURCE_KIND_OPTIONS.find((option) => option.value === source.kind)?.label ?? source.name} · {source.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelClass(isLight)}>Status</label>
        <select className={fieldClass(isLight)} value={status} onChange={(event) => setStatus(event.target.value as PublishStatus)}>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
      </div>
    </DialogShell>
  );
}
